// Manager — "Guide me through a bereavement experience" (PO 2026-08-06).
//
// Seven stages, in order, from hearing the news to the anniversary, with what
// the manager needs to do at each. Content and its rationale live in
// `@besideu/shared/bereavement-guide`; this file is presentation only.
//
// READABLE WITH NOTHING SET UP. No query, no case, no org data, no Maeve call
// — it renders identically for a manager who signed in ninety seconds ago and
// one running four pathways. That is the point: the PO's note is about the
// first session, when every other Manager surface needs a pathway to be useful.
//
// Stages are open by default rather than collapsed. A manager reads this once,
// cold, usually in a hurry, and a wall of closed accordions optimises for
// re-visiting a document you already know the shape of. The in-page contents
// list at the top is the "I only need the return-to-work bit" path, and it is
// plain anchor links — no JS, so it survives being printed, which managers do.
//
// The escalation block renders ABOVE the stages, not appended at the end,
// because it is the one section whose cost of being missed is not "read it
// later". Its content is stage-independent for the same reason.

import { Link } from 'react-router';
import {
  BEREAVEMENT_GUIDE,
  BEREAVEMENT_GUIDE_ESCALATION,
  BEREAVEMENT_GUIDE_STAGES,
} from '@besideu/shared/bereavement-guide';
import { Card } from '@/components/ui/Card.tsx';

export function ManagerBereavementGuide() {
  return (
    <section className="mx-auto max-w-3xl space-y-6">
      <header>
        <h1 className="font-heading text-2xl font-bold tracking-tight">
          Supporting someone through a bereavement
        </h1>
        <p className="mt-2 text-muted-foreground">
          What tends to happen at each stage, and what you&rsquo;ll need to do. This is general
          guidance — it isn&rsquo;t about anyone in particular, and nothing here is recorded against
          anyone.
        </p>
      </header>

      {/* Jump list. Anchors, not state — see the header note on printing. */}
      <nav aria-label="Stages" className="rounded-lg border border-border bg-muted/40 p-4">
        <h2 className="text-sm font-semibold text-foreground">On this page</h2>
        <ol className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          {BEREAVEMENT_GUIDE_STAGES.map((stage, i) => (
            <li key={stage}>
              <a href={`#${stage}`} className="text-primary underline-offset-4 hover:underline">
                {i + 1}. {BEREAVEMENT_GUIDE[stage].title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <EscalationCard />

      <ol className="space-y-4">
        {BEREAVEMENT_GUIDE_STAGES.map((stage, i) => {
          const entry = BEREAVEMENT_GUIDE[stage];
          // Anchor target sits on the `li`, not the `Card` — `Card` is a
          // styling primitive with no `id` prop, and adding one for a single
          // caller is worse than putting it on the element that already
          // exists. `scroll-mt` keeps the heading clear of the app shell's
          // sticky header when the jump list lands on it.
          return (
            <li key={stage} id={stage} className="scroll-mt-20">
              <Card className="space-y-3">
                <div>
                  <p className="text-[0.68rem] font-semibold tracking-[0.16em] text-primary uppercase">
                    Stage {i + 1} · {entry.when}
                  </p>
                  <h2 className="mt-1 font-heading text-lg font-bold">{entry.title}</h2>
                </div>

                <p className="text-sm text-muted-foreground">{entry.what}</p>

                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    What you&rsquo;ll need to do
                  </h3>
                  <ul className="mt-2 space-y-2">
                    {entry.you.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm">
                        <span aria-hidden className="mt-1 text-primary">
                          &bull;
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {entry.avoid ? (
                  // Amber tint + the `-text` token, not the base one: the base
                  // amber fails AA as foreground text (see the StatusPill note
                  // in PROJECT.md's design-system section).
                  <p className="rounded-md border border-traffic-amber/40 bg-traffic-amber/10 p-3 text-sm">
                    <span className="font-medium text-traffic-amber-text">Easy to get wrong: </span>
                    {entry.avoid}
                  </p>
                ) : null}
              </Card>
            </li>
          );
        })}
      </ol>

      <div className="rounded-lg border border-dashed border-border bg-muted/40 p-5">
        <h2 className="text-base font-semibold">When it stops being general</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Opening a support pathway turns the stages above into actual dates, with reminders, and
          gives Maeve the context to help with each one. If you need wording before that,{' '}
          <Link to="/manager/draft" className="text-primary underline-offset-4 hover:underline">
            Maeve can draft a response
          </Link>{' '}
          without you setting anything up. Your organisation&rsquo;s own policy is under{' '}
          <Link to="/manager/policies" className="text-primary underline-offset-4 hover:underline">
            Policies
          </Link>{' '}
          and always takes precedence over anything here.
        </p>
      </div>
    </section>
  );
}

// Red-tinted and above the fold. See the header note on why this is separate
// from the stages rather than repeated inside them.
function EscalationCard() {
  return (
    <div className="rounded-lg border border-traffic-red/40 bg-traffic-red/5 p-5">
      <h2 className="font-heading text-base font-bold text-traffic-red">
        {BEREAVEMENT_GUIDE_ESCALATION.heading}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{BEREAVEMENT_GUIDE_ESCALATION.body}</p>
      <ul className="mt-3 space-y-2">
        {BEREAVEMENT_GUIDE_ESCALATION.actions.map((action) => (
          <li key={action} className="flex items-start gap-2 text-sm">
            <span aria-hidden className="mt-1 text-traffic-red">
              &bull;
            </span>
            <span>{action}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
